import { useState } from "react";
import { copyInviteText, copyJoinUrl } from "../lib/invite";
import { Button } from "./ui";

export type InviteKind = "fun" | "link";

/**
 * Copy either a full fun invite blurb or a naked join URL (code in query).
 * Available to every player, not just host.
 */
export function InviteButton({
  code,
  kind = "fun",
  variant = "secondary",
  className = "",
  label,
  compact = false,
}: {
  code: string;
  kind?: InviteKind;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  className?: string;
  label?: string;
  compact?: boolean;
}) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");

  const defaultLabel = kind === "link" ? "Copy link" : "Copy fun invite";
  const idleLabel = label ?? defaultLabel;

  const onCopy = async () => {
    const ok =
      kind === "link" ? await copyJoinUrl(code) : await copyInviteText(code);
    setStatus(ok ? "copied" : "failed");
    window.setTimeout(() => setStatus("idle"), 2000);
  };

  const text =
    status === "copied"
      ? kind === "link"
        ? "Link copied!"
        : "Invite copied!"
      : status === "failed"
        ? "Copy failed"
        : idleLabel;

  const icon = kind === "link" ? "🔗" : "✉️";
  const title =
    kind === "link"
      ? "Copy join URL only (room code included)"
      : "Copy a fun invite with the room code and link";

  return (
    <Button
      type="button"
      variant={variant}
      className={className}
      onClick={onCopy}
      title={title}
    >
      {compact ? text : `${icon} ${text}`}
    </Button>
  );
}

/** Fun invite + naked URL, side by side. */
export function InvitePair({
  code,
  className = "",
  buttonClassName = "",
  compact = false,
  variant = "secondary",
}: {
  code: string;
  className?: string;
  buttonClassName?: string;
  compact?: boolean;
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  return (
    <div
      className={
        className ||
        "flex w-full flex-col gap-2 sm:flex-row sm:justify-center"
      }
    >
      <InviteButton
        code={code}
        kind="fun"
        variant={variant}
        compact={compact}
        className={buttonClassName || "flex-1 sm:flex-none"}
        label={compact ? "Invite" : "Copy fun invite"}
      />
      <InviteButton
        code={code}
        kind="link"
        variant={variant}
        compact={compact}
        className={buttonClassName || "flex-1 sm:flex-none"}
        label={compact ? "Link" : "Copy link only"}
      />
    </div>
  );
}
